import { Panel as _Panel } from '#~/common/editor/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { useVideoTrack } from '#~/livekit/hooks.js';
import { Protocol as ControlProtocol } from '@studio/models/control';
import { HttpDefaults } from '@studio/models/control/http';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { Device } from '@studio/models/device';
import { toUri } from '@studio/models/video';
import { useDeepEffect, useLatestFn } from '@studio/react-utils/hooks';
import { isNotNil } from '@tryiris-ai/js-types';
import { omit } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import type { Draft } from '../store/types.js';
import { Panel as WorkflowPanel } from '../store/types.js';
import { Content } from './content.jsx';
import { Footer } from './footer.jsx';
import {
  buildBrandOnlyVideoMarker,
  resolveSeededBrandId,
  resolveSeededHost,
  shouldProbeKnownModel
} from './utils.js';

/** Source `<Panel>` component. */
export const Panel = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const selection = useStore(({ source }) => source.selection);
  // In bulk mode the selection sync is irrelevant.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const bulkMode = useStore(({ source }) => source.bulkMode);

  // As soon as the candidate has something that resembles a video URI, we can continue.
  const previewDeviceID = useStore((µ) => {
    const config = µ.candidate.video;

    const value = config ? isNotNil(toUri(config)) : false;

    const videoPreviewDeviceID = µ.previews.video?.deviceId;

    return value ? videoPreviewDeviceID : undefined;
  });

  const previewTrack = useVideoTrack(previewDeviceID ?? ('' as Device['id']));

  const isReady = !!previewTrack;

  // Model-registry template for the selected discovery.
  const selectionModelId = selection?.modelId;
  const template = useTemplate(selectionModelId);
  const saveStatus = useStore(({ finalSave }) => finalSave?.status);
  // A sync skipped while Save was pending; replayed once that save settles without saving.
  const deferredSync = useRef(false);

  // Keep the candidate device in sync with the selected discovery.
  //
  // Two flavors:
  //
  // 1. Generic discovery (no modelId): mirror the selection verbatim into
  //    the candidate (legacy behavior — the config panels configure it
  //    from there).
  //
  // 2. Fingerprint-matched discovery (selection.modelId present): force
  //    `candidate.control = { protocol: HTTP, host, port: 80 }` before the
  //    video/control templates are materialized. This tells the preview
  //    mediator to add a tmp device with `http://host:80` whose sole
  //    purpose is probing credentials via the model's HTTP module Login.
  //    The HTTP driver Build will return an error containing `401:
  //    Unauthorized` (or 403) when the probe rejects the creds — that
  //    surfaces through `previews.control.error`, and footer.tsx's
  //    existing requiresAuth check (same path ONVIF uses) flips on. Once
  //    the HTTP probe succeeds, discovery.tsx swaps in the real video
  //    config and the native control config (e.g. VISCA_IP) from the
  //    template, and the flow commits normally on Continue.
  //
  //    Why force HTTP (not the template's native control)? Because the
  //    native control (VISCA_IP over UDP) has no authentication — there's
  //    nothing to probe. HTTP + module Login is the common denominator
  //    that works for every vendor whose registry entry defines a login.
  // Brand-level fingerprint match (Change 2B): the encoder resolved a
  // `brandId` but NO specific `modelId`. Its `video.path` is empty, so a
  // preview can NEVER connect — exactly the situation the Iris-enabled
  // exception handles by attempting no connection. We mirror that precedent:
  // panel.tsx seeds `source.seededBrandId` instead of forcing the HTTP probe
  // control (which the known-model branch below does for full matches), the
  // discovery.tsx selector renders a "brand matched" badge instead of
  // "Connecting…", and footer.tsx::handleNext jumps straight to Panel.Video
  // (the RTSP Helper) bypassing the isPreviewConnected gate. Iris-enabled is
  // still its own case (selection.irisEnabled) — this is a sibling exception.
  const isBrandOnlyMatch = !!(selection?.video && selection.brandId && !selection.modelId);

  const handleSelectionChange = useLatestFn(async () => {
    const base = selection ? omit(selection, 'id') : {};

    // Source step only: Scaffold keeps this panel mounted through its exit fade, and a
    // selection or template change landing then must not reset the next step's candidate.
    // The step is read from the draft at write time, not at render.
    // A pending Save owns its candidate: a late template or row refresh must not supersede it.
    const syncCandidate = (recipe: (draft: Draft) => void): void => {
      producer((draft) => {
        if (draft.panel !== WorkflowPanel.Source) return;
        const pending = draft.finalSave?.status === 'pending';
        deferredSync.current = pending;
        if (!pending) recipe(draft);
      });
    };

    if (isBrandOnlyMatch && selection) {
      // Brand-only match: attempt NO connection (mirror Iris-enabled). We stash
      // the resolved brand id so rtsp.tsx can pre-seed its Brand dropdown on
      // mount. We do NOT set candidate.control = HTTP here (the known-model
      // probe path), because there is no model to validate creds against — the
      // user picks the model in the RTSP Helper.
      //
      // bug1: STRIP the connectable video/control/audio aspects from the synced
      // candidate. Syncing the discovery's `video` (host, empty path) verbatim
      // made the preview mediator (store/mediator.tsx) add a transient encoder
      // device that attempts an RTSP connection to `host` with no path → FAILED
      // → the sticky "Connection failed" Callout fired in the RTSP Helper before
      // the user had entered ANYTHING. A brand-only match "attempts no
      // connection" (like Iris-enabled), so it must carry no connectable aspect.
      // The discovered host is preserved out-of-band in `source.seededHost` so
      // rtsp.tsx still pre-fills the IP field. Per `reference-rules-in-comments`
      // + `think-in-systems` (ai.knowledge/global).
      syncCandidate((draft) => {
        // B19: clear any prior operator Skip like the sibling branches below.
        // Picking a brand-only row is a fresh discovery selection, so a Skip
        // from a previously-selected row must not leak onto it.
        draft.authSkipped = false;
        draft.candidate = omit(base, ['video', 'control', 'audio']);
        // Seed a BARE RTSP video marker (protocol only, NO host) so
        // content.tsx (`isRtsp(candidate.video?.protocol) && <Rtsp/>`) renders
        // the RTSP Helper — stripping video entirely left protocol undefined and
        // the Configure-video screen blank. host:undefined means the preview
        // Mediator's toUrx builds no connectable device → still no spurious
        // "Connection failed". Mirrors handleManualRtsp (footer.tsx). The
        // discovered host rides in source.seededHost to pre-fill the IP field.
        draft.candidate.video = buildBrandOnlyVideoMarker();
        draft.source.seededBrandId = resolveSeededBrandId(selection);
        draft.source.seededHost = resolveSeededHost(selection);
      });
      return;
    }

    if (selection?.irisEnabled) {
      // An Iris Enabled camera links as its own agent and the bridge has no Iris
      // video driver, so a synced `iris://` aspect only made the Mediator add a
      // preview that could never connect ("Camera Unreachable"). Carry no
      // connectable aspect; Continue opens the credentials dialog instead.
      syncCandidate((draft) => {
        draft.authSkipped = false;
        draft.candidate = omit(base, ['video', 'control', 'audio']);
        draft.source.seededBrandId = resolveSeededBrandId(selection);
        draft.source.seededHost = resolveSeededHost(selection);
      });
      return;
    }

    // The `selection`/`template`/`'video' in selection` repeats are the
    // predicate's own guards, kept here purely so TypeScript narrows the
    // types for the block below (it can't see through the helper's return).
    if (
      shouldProbeKnownModel(selection, template) &&
      selection &&
      template &&
      'video' in selection
    ) {
      // The video aspect's host — what the HTTP probe binds to.
      const selectionVideo = selection.video as { host?: string };
      const host = selectionVideo.host;

      syncCandidate((draft) => {
        // Clear any prior operator Skip on a new discovery row.
        draft.authSkipped = false;
        // Strip video/control/audio; they get rebuilt from the template.
        draft.candidate = omit(base, ['video', 'control', 'audio']);
        if (host) {
          draft.candidate.control = {
            protocol: ControlProtocol.HTTP,
            host,
            port: HttpDefaults.port
          };
        }
        if (template.audio !== undefined) {
          draft.candidate.audio = { ...template.audio };
        }
        draft.candidate.modelId = selection.modelId;
        draft.candidate.name = template.model?.name ?? selection.name;
        // Clear any stale brand-only seed (Change 2B): this is a full model
        // match, not a brand-only one (resolver returns undefined here).
        draft.source.seededBrandId = resolveSeededBrandId(selection);
        // bug1: clear the brand-only host seed too (a full-model match owns
        // the candidate's video aspect; resolver returns undefined here).
        draft.source.seededHost = resolveSeededHost(selection);
      });
    } else {
      syncCandidate((draft) => {
        // Clear any prior operator Skip whenever the selection changes.
        draft.authSkipped = false;
        // Legacy: sync candidate with selection. Don't do anything fancy.
        draft.candidate = base;
        // Clear any stale brand-only seed (Change 2B) for non-brand-only rows
        // (resolver returns undefined for a legacy/no-model selection).
        draft.source.seededBrandId = resolveSeededBrandId(selection);
        // bug1: clear the brand-only host seed too (resolver returns undefined
        // for a legacy/no-model selection).
        draft.source.seededHost = resolveSeededHost(selection);
      });
    }
  });

  // Skip single-device candidate sync in bulk mode.
  useDeepEffect(() => {
    if (bulkMode === true) return;

    handleSelectionChange();
  }, [selection, template, bulkMode]);

  // Retryable, superseded or cleared: Retry must start from the row as it is now.
  // Saved or uncertain may already be on the Bridge, so those keep the candidate.
  useEffect(() => {
    if (!deferredSync.current || saveStatus === 'pending') return;
    deferredSync.current = false;
    if (bulkMode === true || saveStatus === 'saved' || saveStatus === 'uncertain') return;
    handleSelectionChange();
  }, [saveStatus, bulkMode, handleSelectionChange]);

  useEffect(() => {
    producer(({ source }) => {
      source.isReady = isReady;
    });
  }, [isReady]);

  return (
    <_Panel {...props}>
      <Content />
      <Footer />
    </_Panel>
  );
};
